import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix default marker icon issue with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const MARKER_COLORS = {
  current: '#3E6F86',
  closed: '#3E6F86',
  highlight: '#E0B341',
};

// Multi-leg spheroid water tower — the iconic American small-town silhouette.
// Spherical/ellipsoid bowl on a splayed 4-leg "A" frame with cross-bracing.
// viewBox 32x48 so legs splay wider than the bowl at the base.
// eslint-disable-next-line no-unused-vars
function createMultiLegTowerIcon(color) {
  return L.divIcon({
    html: `<svg width="32" height="48" viewBox="0 0 32 48" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Multi-leg spheroid water tower">
      <!-- Finial / antenna spike on top -->
      <line x1="16" y1="0.6" x2="16" y2="4.2" stroke="${color}" stroke-width="1" stroke-linecap="round"/>
      <circle cx="16" cy="0.6" r="0.8" fill="${color}"/>

      <!-- Spherical/ellipsoidal bowl. Slightly squashed vertically. -->
      <ellipse cx="16" cy="12" rx="9.5" ry="7.5" fill="${color}" stroke="white" stroke-width="0.8"/>

      <!-- Subtle highlight on the bowl -->
      <path d="M 10 9 Q 11 6 14.5 5.5"
            stroke="white" stroke-width="0.9" stroke-linecap="round" opacity="0.55" fill="none"/>

      <!-- Short collar where the bowl meets the legs -->
      <rect x="13" y="18.5" width="6" height="1.6" fill="${color}" stroke="white" stroke-width="0.4"/>

      <!-- 4 splayed legs forming a tapered "A" frame.
           Two outer legs visible on the silhouette + two inner legs (lighter
           stroke) for depth. -->
      <!-- Outer left -->
      <line x1="9" y1="20" x2="2.5" y2="44" stroke="${color}" stroke-width="1.6" stroke-linecap="round"/>
      <!-- Outer right -->
      <line x1="23" y1="20" x2="29.5" y2="44" stroke="${color}" stroke-width="1.6" stroke-linecap="round"/>
      <!-- Inner left (slightly thinner — back leg in perspective) -->
      <line x1="13" y1="20" x2="10" y2="44" stroke="${color}" stroke-width="1.1" stroke-linecap="round" opacity="0.85"/>
      <!-- Inner right -->
      <line x1="19" y1="20" x2="22" y2="44" stroke="${color}" stroke-width="1.1" stroke-linecap="round" opacity="0.85"/>

      <!-- Cross-bracing between the outer legs (one X mid-height + a horizontal tie) -->
      <line x1="5.5" y1="31" x2="26.5" y2="38" stroke="${color}" stroke-width="0.7" stroke-linecap="round" opacity="0.85"/>
      <line x1="26.5" y1="31" x2="5.5" y2="38" stroke="${color}" stroke-width="0.7" stroke-linecap="round" opacity="0.85"/>
      <line x1="6.5" y1="27" x2="25.5" y2="27" stroke="${color}" stroke-width="0.55" stroke-linecap="round" opacity="0.75"/>

      <!-- Small feet/footings at each outer leg base -->
      <line x1="1" y1="45" x2="4.5" y2="45" stroke="${color}" stroke-width="1.4" stroke-linecap="round"/>
      <line x1="27.5" y1="45" x2="31" y2="45" stroke="${color}" stroke-width="1.4" stroke-linecap="round"/>
    </svg>`,
    className: 'custom-marker',
    iconSize: [32, 48],
    iconAnchor: [16, 46],
    popupAnchor: [0, -46],
  });
}

// Kept for future "tank type" toggle — composite pedestal silhouette.
// Use createMultiLegTowerIcon as the default map marker.
// eslint-disable-next-line no-unused-vars
function createCompositeTowerIcon(color) {
  return L.divIcon({
    html: `<svg width="28" height="48" viewBox="0 0 28 48" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Composite elevated water tank">
      <line x1="14" y1="0.5" x2="14" y2="4" stroke="${color}" stroke-width="1" stroke-linecap="round"/>
      <circle cx="14" cy="0.6" r="0.7" fill="${color}"/>
      <path d="M 11 16 Q 8.5 12.5 8 9 Q 8 4 14 4 Q 20 4 20 9 Q 19.5 12.5 17 16 Z"
            fill="${color}" stroke="white" stroke-width="0.8" stroke-linejoin="round"/>
      <path d="M 11 16 L 12 18 L 16 18 L 17 16 Z"
            fill="${color}" stroke="white" stroke-width="0.5" stroke-linejoin="round"/>
      <rect x="12" y="18" width="4" height="25" fill="${color}" stroke="white" stroke-width="0.6"/>
      <path d="M 9 43 L 19 43 L 20 47 L 8 47 Z"
            fill="${color}" stroke="white" stroke-width="0.6" stroke-linejoin="round"/>
    </svg>`,
    className: 'custom-marker',
    iconSize: [28, 48],
    iconAnchor: [14, 47],
    popupAnchor: [0, -48],
  });
}

// Clean circular map dots (replaces the water-tower silhouette markers).
function createCircleIcon(color, selected) {
  const size = selected ? 18 : 13;
  const shadow = selected
    ? 'box-shadow:0 0 0 4px rgba(224,179,65,0.25),0 1px 5px rgba(0,0,0,0.55);'
    : 'box-shadow:0 1px 4px rgba(0,0,0,0.5);';
  return L.divIcon({
    html: `<span style="display:block;width:${size}px;height:${size}px;border-radius:9999px;background:${color};border:2px solid #ffffff;${shadow}"></span>`,
    className: 'custom-marker',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -(size / 2) - 2],
  });
}

export const ICONS = {
  current: createCircleIcon(MARKER_COLORS.current, false),
  closed: createCircleIcon(MARKER_COLORS.closed, false),
  highlight: createCircleIcon(MARKER_COLORS.highlight, true),
};

/**
 * Decide which marker icon to use for a given project location.
 * @param {{status: string, id: number}} location
 * @param {number|null|undefined} selectedId
 */
export function getMarkerIcon(location, selectedId) {
  if (selectedId === location.id) return ICONS.highlight;
  if (location.status === 'current') return ICONS.current;
  return ICONS.closed;
}

export { MARKER_COLORS };
